import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
export type ToolCategory = 'CRM' | 'Finance' | 'Communication' | 'Productivity' | 'Sales' | 'Development' | 'Security';
export type ToolType = 'API' | 'Function' | 'DB' | 'RAG' | 'Custom';
export type ToolStatus = 'connected' | 'disconnected' | 'archived';
export interface Tool {
  id: string;
  name: string;
  slug: string;
  category: ToolCategory;
  description: string;
  type: ToolType;
  provider: 'Built-in' | 'Custom' | 'Marketplace';
  status: ToolStatus;
  iconUrl: string;
  tags: string[];
  isFavorite: boolean;
  price: number;
  metrics: {
    calls: number;
    avgCost: number;
    successRate: number;
    latency: number;
  };
}
interface ToolsState {
  tools: Tool[];
  viewMode: 'grid' | 'table';
  activeToolId: string | null;
  addTool: (tool: Tool) => void;
  updateTool: (id: string, updates: Partial<Tool>) => void;
  deleteTool: (id: string) => void;
  toggleFavorite: (id: string) => void;
  toggleViewMode: () => void;
  setActiveToolId: (id: string | null) => void;
}

const SAMPLE_TOOLS: Tool[] = [
  {
    id: 'tool-1',
    name: 'Google Search',
    slug: 'google-search',
    category: 'Productivity',
    description: 'Enables real-time web searching capabilities for agents.',
    type: 'API',
    provider: 'Built-in',
    status: 'connected',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/2991/2991148.png',
    tags: ['search', 'web'],
    isFavorite: true,
    price: 0,
    metrics: { calls: 15402, avgCost: 0.002, successRate: 99.9, latency: 450 }
  },
  {
    id: 'tool-2',
    name: 'Gmail Integration',
    slug: 'gmail',
    category: 'Communication',
    description: 'Read, write, and send emails via enterprise Gmail accounts.',
    type: 'API',
    provider: 'Built-in',
    status: 'connected',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/5968/5968534.png',
    tags: ['email', 'google'],
    isFavorite: true,
    price: 0,
    metrics: { calls: 8234, avgCost: 0.005, successRate: 98.5, latency: 620 }
  },
  {
    id: 'tool-3',
    name: 'HubSpot CRM',
    slug: 'hubspot',
    category: 'CRM',
    description: 'Sync contacts, deals, and activities with HubSpot.',
    type: 'API',
    provider: 'Marketplace',
    status: 'connected',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/5968/5968853.png',
    tags: ['crm', 'sales'],
    isFavorite: false,
    price: 19,
    metrics: { calls: 3450, avgCost: 0.012, successRate: 97.2, latency: 850 }
  },
  {
    id: 'tool-4',
    name: 'Stripe Finance',
    slug: 'stripe',
    category: 'Finance',
    description: 'Manage payments, refunds, and subscription status.',
    type: 'API',
    provider: 'Built-in',
    status: 'connected',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/5968/5968382.png',
    tags: ['finance', 'payments'],
    isFavorite: false,
    price: 0,
    metrics: { calls: 1205, avgCost: 0.008, successRate: 100, latency: 320 }
  },
  {
    id: 'tool-5',
    name: 'Custom Python Executor',
    slug: 'python-executor',
    category: 'Development',
    description: 'Secure sandbox for executing dynamic Python scripts.',
    type: 'Function',
    provider: 'Custom',
    status: 'connected',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/5968/5968350.png',
    tags: ['code', 'python'],
    isFavorite: true,
    price: 0,
    metrics: { calls: 4500, avgCost: 0.001, successRate: 95.5, latency: 1200 }
  }
];

const SEEDED_TOOLS: Tool[] = [
  { id: 'tool-6', name: 'Tool Delta 6', slug: 'tool-delta-6', category: 'Security', description: 'Enterprise-grade tool number 6 for advanced business logic.', type: 'API', provider: 'Marketplace', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 9.99, metrics: { calls: 4120, avgCost: 0.003, successRate: 96.2, latency: 540 } },
  { id: 'tool-7', name: 'Tool Delta 7', slug: 'tool-delta-7', category: 'Sales', description: 'Enterprise-grade tool number 7 for advanced business logic.', type: 'API', provider: 'Custom', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 0, metrics: { calls: 1850, avgCost: 0.003, successRate: 92.4, latency: 880 } },
  { id: 'tool-8', name: 'Tool Delta 8', slug: 'tool-delta-8', category: 'Security', description: 'Enterprise-grade tool number 8 for advanced business logic.', type: 'API', provider: 'Custom', status: 'disconnected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 0, metrics: { calls: 2310, avgCost: 0.003, successRate: 91.7, latency: 960 } },
  { id: 'tool-9', name: 'Tool Delta 9', slug: 'tool-delta-9', category: 'Sales', description: 'Enterprise-grade tool number 9 for advanced business logic.', type: 'API', provider: 'Marketplace', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 9.99, metrics: { calls: 3670, avgCost: 0.003, successRate: 98.1, latency: 420 } },
  { id: 'tool-10', name: 'Tool Delta 10', slug: 'tool-delta-10', category: 'Security', description: 'Enterprise-grade tool number 10 for advanced business logic.', type: 'API', provider: 'Custom', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 0, metrics: { calls: 2980, avgCost: 0.003, successRate: 94.8, latency: 610 } },
  { id: 'tool-11', name: 'Tool Delta 11', slug: 'tool-delta-11', category: 'Sales', description: 'Enterprise-grade tool number 11 for advanced business logic.', type: 'API', provider: 'Custom', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 0, metrics: { calls: 1430, avgCost: 0.003, successRate: 90.9, latency: 1020 } },
  { id: 'tool-12', name: 'Tool Delta 12', slug: 'tool-delta-12', category: 'Security', description: 'Enterprise-grade tool number 12 for advanced business logic.', type: 'API', provider: 'Marketplace', status: 'disconnected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 9.99, metrics: { calls: 2210, avgCost: 0.003, successRate: 93.6, latency: 770 } },
  { id: 'tool-13', name: 'Tool Delta 13', slug: 'tool-delta-13', category: 'Sales', description: 'Enterprise-grade tool number 13 for advanced business logic.', type: 'API', provider: 'Custom', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 0, metrics: { calls: 4095, avgCost: 0.003, successRate: 97.4, latency: 500 } },
  { id: 'tool-14', name: 'Tool Delta 14', slug: 'tool-delta-14', category: 'Security', description: 'Enterprise-grade tool number 14 for advanced business logic.', type: 'API', provider: 'Custom', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 0, metrics: { calls: 3360, avgCost: 0.003, successRate: 95.1, latency: 650 } },
  { id: 'tool-15', name: 'Tool Delta 15', slug: 'tool-delta-15', category: 'Sales', description: 'Enterprise-grade tool number 15 for advanced business logic.', type: 'API', provider: 'Marketplace', status: 'connected', iconUrl: 'https://cdn-icons-png.flaticon.com/512/10434/10434151.png', tags: ['enterprise', 'v2'], isFavorite: false, price: 9.99, metrics: { calls: 2890, avgCost: 0.003, successRate: 96.8, latency: 580 } },
];

const INITIAL_TOOLS = [...SAMPLE_TOOLS, ...SEEDED_TOOLS];
export const useToolsStore = create<ToolsState>()(
  persist(
    (set) => ({
      tools: INITIAL_TOOLS,
      viewMode: 'grid',
      activeToolId: null,
      addTool: (tool) => set((state) => ({ tools: [tool, ...state.tools] })),
      updateTool: (id, updates) => set((state) => ({
        tools: state.tools.map((t) => t.id === id ? { ...t, ...updates } : t)
      })),
      deleteTool: (id) => set((state) => ({
        tools: state.tools.filter((t) => t.id !== id)
      })),
      toggleFavorite: (id) => set((state) => ({
        tools: state.tools.map((t) => t.id === id ? { ...t, isFavorite: !t.isFavorite } : t)
      })),
      toggleViewMode: () => set((state) => ({ viewMode: state.viewMode === 'grid' ? 'table' : 'grid' })),
      setActiveToolId: (id) => set({ activeToolId: id }),
    }),
    {
      name: 'cloudface-tools-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
// Primitive selectors
export const useTools = () => useToolsStore((s) => s.tools);
export const useToolsViewMode = () => useToolsStore((s) => s.viewMode);
export const useActiveToolId = () => useToolsStore((s) => s.activeToolId);
// Action selectors
export const useAddTool = () => useToolsStore((s) => s.addTool);
export const useUpdateTool = () => useToolsStore((s) => s.updateTool);
export const useDeleteTool = () => useToolsStore((s) => s.deleteTool);
export const useToggleFavoriteTool = () => useToolsStore((s) => s.toggleFavorite);
export const useToggleToolViewMode = () => useToolsStore((s) => s.toggleViewMode);
export const useSetActiveToolId = () => useToolsStore((s) => s.setActiveToolId);
